import { useState, useEffect } from "react";
import { packCostBreakdown } from "../../utils/costingUtils";
import { ModalWrap } from "../../components/ModalWrap";
import { useToast } from "../../components/Toast";
import {
  getLargeOrderCostings, getLargeOrderCosting,
  createLargeOrderCosting, updateLargeOrderCosting, deleteLargeOrderCosting,
  fetchHmCycles, fetchHmCycle,
} from "../../apiService";

const PACK_SIZES = [20, 200, 1000];
const MARGINS    = [5, 10, 15, 20, 25];
const fmt2 = n => Number(n).toFixed(2);
const fmt4 = n => Number(n).toFixed(4);

function hmCostPerKg(usdPrice, cycle) {
  if (!cycle || !usdPrice) return 0;
  return Number(usdPrice) * Number(cycle.fxRate)
       * (1 + Number(cycle.customsPct))
       * (1 + Number(cycle.capitalPct))
       + Number(cycle.freightPerKg);
}

function calcSummary(lines, transportCost, additionalOverhead, capitalPct, usdRate) {
  const totalContentCost = lines.reduce((s, l) => s + Number(l.costPerKg) * Number(l.qtyKg), 0);
  const capitalCost      = totalContentCost * (Number(capitalPct) / 100);
  const grandTotal       = totalContentCost + Number(transportCost) + Number(additionalOverhead) + capitalCost;
  const totalQty         = lines.reduce((s, l) => s + Number(l.qtyKg), 0);
  const blended          = totalQty > 0 ? grandTotal / totalQty : 0;
  return { totalContentCost, capitalCost, grandTotal, totalQty, blended, usdRate: Number(usdRate) };
}

function MarginTable({ summary, customPct }) {
  const margins = [...MARGINS, ...(customPct ? [Number(customPct)] : [])];
  return (
    <div className="table-wrap" style={{ marginTop: 16 }}>
      <table className="data-table">
        <thead>
          <tr>
            <th>Margin</th>
            <th style={{ textAlign:"right" }}>Selling/kg (AED)</th>
            <th style={{ textAlign:"right" }}>Selling/kg (USD)</th>
          </tr>
        </thead>
        <tbody>
          {margins.map(m => {
            const sellingPerKg    = summary.blended * (1 + m / 100);
            const sellingPerKgUsd = summary.usdRate > 0 ? sellingPerKg / summary.usdRate : 0;
            return (
              <tr key={m}>
                <td style={{ fontWeight:600 }}>{m}%</td>
                <td style={{ textAlign:"right" }}>{fmt4(sellingPerKg)}</td>
                <td style={{ textAlign:"right", color:"#0369a1" }}>{fmt4(sellingPerKgUsd)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CostingForm({ initial, fgs, fgFormulas, sfgFormulas, rms, settings, hmGrades, hmCycle, onSave, onClose }) {
  const [name,               setName]              = useState(initial?.name ?? "");
  const [notes,              setNotes]             = useState(initial?.notes ?? "");
  const [transportCost,      setTransportCost]     = useState(initial?.transportCost ?? "0");
  const [additionalOverhead, setAdditionalOverhead]= useState(initial?.additionalOverhead ?? "0");
  const [capitalPct,         setCapitalPct]        = useState(initial?.capitalPct ?? "0");
  const [usdRate,            setUsdRate]           = useState(initial?.usdRate ?? "3.6725");
  const [customPct,          setCustomPct]         = useState("");
  const [lines,              setLines]             = useState(initial?.lines ?? []);
  const [saving,             setSaving]            = useState(false);

  const summary = calcSummary(lines, transportCost, additionalOverhead, capitalPct, usdRate);

  // Build a lookup of gradeId → usdPrice from the current HM cycle
  const hmPriceMap = {};
  if (hmCycle?.prices) {
    hmCycle.prices.forEach(p => { hmPriceMap[p.gradeId] = p.usdPrice; });
  }

  function addLine() {
    setLines(prev => [...prev, { productType:"WB", businessUnit:"AMI", fgId:"", fgName:"", packSize:1000, costPerKg:"0", qtyKg:"" }]);
  }

  function removeLine(i) {
    setLines(prev => prev.filter((_, idx) => idx !== i));
  }

  function updateLine(i, field, value) {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  }

  function onProductTypeChange(i, productType) {
    setLines(prev => prev.map((l, idx) => idx === i
      ? { ...l, productType, fgId:"", fgName:"", packSize: productType === "WB" ? 1000 : null, costPerKg:"0" }
      : l));
  }

  function onFgChange(i, fgId) {
    const line = lines[i];
    if (line.productType === "HM") {
      const grade = hmGrades.find(g => g.id === fgId);
      if (!grade) return;
      const cost  = fmt4(hmCostPerKg(hmPriceMap[fgId] ?? 0, hmCycle));
      setLines(prev => prev.map((l, idx) => idx === i
        ? { ...l, fgId: grade.id, fgName: grade.displayName, costPerKg: cost }
        : l));
    } else {
      const fg = fgs.find(f => f.id === fgId);
      if (!fg) return;
      const pkg  = settings.packing.find(p => p.size === Number(line.packSize));
      const cost = pkg ? packCostBreakdown(fg, pkg, fgFormulas, sfgFormulas, rms,
                                           settings.labourPerKg, settings.wastageGlobal ?? 1).perKg : 0;
      setLines(prev => prev.map((l, idx) => idx === i
        ? { ...l, fgId: fg.id, fgName: fg.name, costPerKg: fmt4(cost) }
        : l));
    }
  }

  function onPackSizeChange(i, packSize) {
    const line = lines[i];
    const fg   = fgs.find(f => f.id === line.fgId);
    if (fg) {
      const pkg  = settings.packing.find(p => p.size === Number(packSize));
      const cost = pkg ? packCostBreakdown(fg, pkg, fgFormulas, sfgFormulas, rms,
                                           settings.labourPerKg, settings.wastageGlobal ?? 1).perKg : 0;
      setLines(prev => prev.map((l, idx) => idx === i
        ? { ...l, packSize: Number(packSize), costPerKg: fmt4(cost) }
        : l));
    } else {
      updateLine(i, "packSize", Number(packSize));
    }
  }

  async function handleSave() {
    if (!name.trim() || lines.length === 0) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        notes: notes.trim() || null,
        transportCost:      Number(transportCost),
        additionalOverhead: Number(additionalOverhead),
        capitalPct:         Number(capitalPct),
        usdRate:            Number(usdRate),
        lines: lines.map(l => ({
          productType:  l.productType,
          businessUnit: l.businessUnit,
          fgId:         l.fgId,
          fgName:       l.fgName,
          packSize:     l.packSize ? Number(l.packSize) : null,
          costPerKg:    Number(l.costPerKg),
          qtyKg:        Number(l.qtyKg),
        })),
      });
    } finally {
      setSaving(false);
    }
  }

  const colCount = "2fr 80px 80px 140px 120px 120px 32px";

  return (
    <ModalWrap title={initial ? "Edit Large Order Costing" : "New Large Order Costing"} wide onClose={onClose}>
      <div className="modal-body">

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
          <div className="form-group" style={{ gridColumn:"1/-1" }}>
            <label className="form-label">Name <span style={{ color:"#e00" }}>*</span></label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. ABC Trading — Sep 2026 bulk order" />
          </div>
          <div className="form-group" style={{ gridColumn:"1/-1" }}>
            <label className="form-label">Notes</label>
            <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Optional notes" />
          </div>
          <div className="form-group">
            <label className="form-label">Transport Cost (AED)</label>
            <input className="form-input" type="number" min="0" step="0.01"
              value={transportCost} onChange={e => setTransportCost(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Additional Overheads (AED)</label>
            <input className="form-input" type="number" min="0" step="0.01"
              value={additionalOverhead} onChange={e => setAdditionalOverhead(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Capital Cost (%)</label>
            <input className="form-input" type="number" min="0" step="0.1"
              value={capitalPct} onChange={e => setCapitalPct(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">USD Rate (AED per 1 USD)</label>
            <input className="form-input" type="number" min="0" step="0.0001"
              value={usdRate} onChange={e => setUsdRate(e.target.value)} />
          </div>
        </div>

        <div style={{ marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <strong>Grade Lines</strong>
          <button className="btn-small" onClick={addLine}>+ Add Grade</button>
        </div>
        {lines.length === 0 && (
          <div style={{ color:"#999", fontSize:13, marginBottom:12 }}>No grades added yet — click + Add Grade.</div>
        )}

        {/* Column headers */}
        {lines.length > 0 && (
          <div style={{ display:"grid", gridTemplateColumns:colCount, gap:8, marginBottom:2 }}>
            <label className="form-label" style={{ fontSize:11 }}>Grade</label>
            <label className="form-label" style={{ fontSize:11 }}>Type</label>
            <label className="form-label" style={{ fontSize:11 }}>Entity</label>
            <label className="form-label" style={{ fontSize:11 }}>Pack Size</label>
            <label className="form-label" style={{ fontSize:11 }}>Cost/kg (AED)</label>
            <label className="form-label" style={{ fontSize:11 }}>Qty (kg)</label>
            <div />
          </div>
        )}

        {lines.map((line, i) => (
          <div key={i} style={{ display:"grid", gridTemplateColumns:colCount, gap:8, alignItems:"center", marginBottom:8 }}>
            {/* Grade selector */}
            <select className="form-input" value={line.fgId} onChange={e => onFgChange(i, e.target.value)}>
              <option value="">Select grade…</option>
              {line.productType === "HM"
                ? hmGrades.map(g => <option key={g.id} value={g.id}>{g.displayName}</option>)
                : fgs.map(fg => <option key={fg.id} value={fg.id}>{fg.name}</option>)
              }
            </select>
            {/* Product type */}
            <select className="form-input" value={line.productType} onChange={e => onProductTypeChange(i, e.target.value)}>
              <option value="WB">WB</option>
              <option value="HM">HM</option>
            </select>
            {/* Business unit */}
            <select className="form-input" value={line.businessUnit} onChange={e => updateLine(i, "businessUnit", e.target.value)}>
              <option value="AMI">AMI</option>
              <option value="AMT">AMT</option>
            </select>
            {/* Pack size — only for WB */}
            {line.productType === "WB"
              ? (
                <select className="form-input" value={line.packSize ?? 1000} onChange={e => onPackSizeChange(i, e.target.value)}>
                  {PACK_SIZES.map(s => <option key={s} value={s}>{s} kg</option>)}
                </select>
              ) : (
                <div style={{ fontSize:12, color:"#999", paddingLeft:4 }}>—</div>
              )
            }
            {/* Cost per kg */}
            <input className="form-input" type="number" min="0" step="0.0001" value={line.costPerKg}
              onChange={e => updateLine(i, "costPerKg", e.target.value)} />
            {/* Qty */}
            <input className="form-input" type="number" min="0" step="1" value={line.qtyKg}
              onChange={e => updateLine(i, "qtyKg", e.target.value)} placeholder="0" />
            <button className="btn-small" style={{ color:"#e00" }} onClick={() => removeLine(i)}>✕</button>
          </div>
        ))}

        {summary.totalQty > 0 && (
          <div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:6, padding:12, marginTop:8 }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:8 }}>
              <div>
                <div style={{ fontSize:11, color:"#666" }}>Content Cost</div>
                <div style={{ fontWeight:600 }}>AED {fmt2(summary.totalContentCost)}</div>
              </div>
              <div>
                <div style={{ fontSize:11, color:"#666" }}>Capital Cost</div>
                <div style={{ fontWeight:600 }}>AED {fmt2(summary.capitalCost)}</div>
              </div>
              <div>
                <div style={{ fontSize:11, color:"#666" }}>Grand Total</div>
                <div style={{ fontWeight:600 }}>AED {fmt2(summary.grandTotal)}</div>
              </div>
              <div>
                <div style={{ fontSize:11, color:"#666" }}>Blended Cost/kg</div>
                <div style={{ fontWeight:600, color:"#1d4ed8" }}>AED {fmt4(summary.blended)}</div>
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
              <span style={{ fontSize:12, color:"#555" }}>Custom margin %:</span>
              <input style={{ width:80 }} className="form-input" type="number" min="0" step="0.5"
                value={customPct} onChange={e => setCustomPct(e.target.value)} placeholder="e.g. 18" />
            </div>
            <MarginTable summary={summary} customPct={customPct} />
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name.trim() || lines.length === 0 || saving} onClick={handleSave}>
          {saving ? "Saving…" : (initial ? "Save Changes" : "Create")}
        </button>
      </div>
    </ModalWrap>
  );
}

export function PageLargeOrderCosting({ fgs, fgFormulas, sfgFormulas, rms, settings, hmGrades }) {
  const showToast = useToast();
  const [costings,    setCostings]    = useState([]);
  const [modal,       setModal]       = useState(null);
  const [loadingView, setLoadingView] = useState(false);
  const [hmCycle,     setHmCycle]     = useState(null);

  useEffect(() => {
    getLargeOrderCostings()
      .then(setCostings)
      .catch(e => console.error("Failed to load large order costings:", e));

    fetchHmCycles()
      .then(cycles => {
        if (cycles.length === 0) return;
        const latestSlug = cycles[cycles.length - 1].slug;
        return fetchHmCycle(latestSlug);
      })
      .then(cycle => { if (cycle) setHmCycle(cycle); })
      .catch(e => console.error("Failed to load HM cycle:", e));
  }, []);

  async function handleCreate(body) {
    const saved = await createLargeOrderCosting(body);
    setCostings(prev => [saved, ...prev]);
    setModal(null);
    showToast("Large order costing saved");
  }

  async function handleUpdate(id, body) {
    const updated = await updateLargeOrderCosting(id, body);
    setCostings(prev => prev.map(c => c.id === id ? updated : c));
    setModal(null);
    showToast("Large order costing updated");
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this costing? This cannot be undone.")) return;
    await deleteLargeOrderCosting(id);
    setCostings(prev => prev.filter(c => c.id !== id));
    showToast("Deleted");
  }

  async function handleView(costing) {
    setLoadingView(true);
    try {
      const full = await getLargeOrderCosting(costing.id);
      setModal({ type:"view", data: full });
    } catch (e) {
      alert("Failed to load: " + e.message);
    } finally {
      setLoadingView(false);
    }
  }

  async function handleEditOpen(costing) {
    setLoadingView(true);
    try {
      const full = await getLargeOrderCosting(costing.id);
      setModal({ type:"edit", data: full });
    } catch (e) {
      alert("Failed to load: " + e.message);
    } finally {
      setLoadingView(false);
    }
  }

  const formProps = { fgs, fgFormulas, sfgFormulas, rms, settings, hmGrades: hmGrades ?? [], hmCycle };

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Large Order Costing</div>
          <div className="page-subtitle">Blended cost &amp; margin calculator for bulk orders</div>
        </div>
        <div className="page-header-right">
          <button className="btn-primary" onClick={() => setModal({ type:"create" })}>+ New Costing</button>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Date</th>
                <th>Notes</th>
                <th>Created by</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {costings.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign:"center", color:"#999", padding:24 }}>
                  No costings yet — click + New Costing to start.
                </td></tr>
              )}
              {costings.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight:600 }}>{c.name}</td>
                  <td style={{ color:"#555", fontSize:13 }}>{c.createdAt?.slice(0,10)}</td>
                  <td style={{ color:"#666", fontSize:13 }}>{c.notes || "—"}</td>
                  <td style={{ color:"#555", fontSize:13 }}>{c.createdBy}</td>
                  <td style={{ display:"flex", gap:6 }}>
                    <button className="btn-small" disabled={loadingView} onClick={() => handleView(c)}>View</button>
                    <button className="btn-small" disabled={loadingView} onClick={() => handleEditOpen(c)}>Edit</button>
                    <button className="btn-small" style={{ color:"#e00" }} onClick={() => handleDelete(c.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal?.type === "create" && (
        <div className="overlay">
          <CostingForm {...formProps} onSave={handleCreate} onClose={() => setModal(null)} />
        </div>
      )}

      {modal?.type === "edit" && (
        <div className="overlay">
          <CostingForm {...formProps} initial={modal.data}
            onSave={body => handleUpdate(modal.data.id, body)}
            onClose={() => setModal(null)}
          />
        </div>
      )}

      {modal?.type === "view" && (() => {
        const d       = modal.data;
        const summary = calcSummary(d.lines, d.transportCost, d.additionalOverhead, d.capitalPct, d.usdRate);
        return (
          <div className="overlay">
            <ModalWrap title={d.name} wide onClose={() => setModal(null)}>
              <div className="modal-body">
                {d.notes && <p style={{ color:"#555", fontSize:13, marginBottom:12 }}>{d.notes}</p>}

                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:16 }}>
                  <div><div style={{ fontSize:11, color:"#666" }}>Transport</div><div>AED {fmt2(d.transportCost)}</div></div>
                  <div><div style={{ fontSize:11, color:"#666" }}>Overheads</div><div>AED {fmt2(d.additionalOverhead)}</div></div>
                  <div><div style={{ fontSize:11, color:"#666" }}>Capital Cost</div><div>{d.capitalPct}%</div></div>
                  <div><div style={{ fontSize:11, color:"#666" }}>USD Rate</div><div>{d.usdRate}</div></div>
                </div>

                <div className="table-wrap" style={{ marginBottom:16 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Grade</th>
                        <th>Type</th>
                        <th>Entity</th>
                        <th style={{ textAlign:"right" }}>Pack Size</th>
                        <th style={{ textAlign:"right" }}>Cost/kg (AED)</th>
                        <th style={{ textAlign:"right" }}>Qty (kg)</th>
                        <th style={{ textAlign:"right" }}>Line Total (AED)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.lines.map(l => (
                        <tr key={l.id}>
                          <td style={{ fontWeight:600 }}>{l.fgName}</td>
                          <td><span style={{ fontSize:11, background: l.productType==="HM" ? "#fdf4ff" : "#eff6ff",
                            color: l.productType==="HM" ? "#7e22ce" : "#1d4ed8",
                            padding:"1px 6px", borderRadius:4, fontWeight:600 }}>{l.productType}</span></td>
                          <td><span style={{ fontSize:11, background:"#f0f9ff", color:"#0369a1",
                            padding:"1px 6px", borderRadius:4, fontWeight:600 }}>{l.businessUnit}</span></td>
                          <td style={{ textAlign:"right" }}>{l.packSize ? `${l.packSize} kg` : "—"}</td>
                          <td style={{ textAlign:"right" }}>{fmt4(l.costPerKg)}</td>
                          <td style={{ textAlign:"right" }}>{fmt2(l.qtyKg)}</td>
                          <td style={{ textAlign:"right" }}>{fmt2(Number(l.costPerKg) * Number(l.qtyKg))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:6, padding:12 }}>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:12 }}>
                    <div>
                      <div style={{ fontSize:11, color:"#666" }}>Total Qty</div>
                      <div style={{ fontWeight:600 }}>{fmt2(summary.totalQty)} kg</div>
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:"#666" }}>Content Cost</div>
                      <div style={{ fontWeight:600 }}>AED {fmt2(summary.totalContentCost)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:"#666" }}>Grand Total</div>
                      <div style={{ fontWeight:600 }}>AED {fmt2(summary.grandTotal)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:"#666" }}>Blended Cost/kg</div>
                      <div style={{ fontWeight:600, color:"#1d4ed8" }}>AED {fmt4(summary.blended)}</div>
                    </div>
                  </div>
                  <MarginTable summary={summary} customPct={null} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={() => setModal(null)}>Close</button>
                <button className="btn-primary" onClick={() => setModal({ type:"edit", data: d })}>Edit</button>
              </div>
            </ModalWrap>
          </div>
        );
      })()}
    </>
  );
}
